import { useState } from 'react';
import { Box, Typography, Paper, Card, CardContent, Chip, CircularProgress, IconButton } from '@mui/material';
import { GripVertical, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNodes, useUpdateNode, usePicklist } from '@lingaai/shared-hooks';
import type { Node } from '@lingaai/types';
import { NODE_REGISTRY as NODE_TYPE_CONFIG } from '@lingaai/types/graph/nodes';
import { NodeDrawer } from '../graph/NodeDrawer';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';

interface PipelineViewProps {
  moduleId: string;
  nodeType: string;
}

// Map moduleId to picklist name
const PIPELINE_PICKLIST_MAP: Record<string, string> = {
  sales: 'opportunity_stage',
  'pevc-dealflow': 'deal_stage',
  'pevc-fundraising': 'fundraising_stage',
};

export function PipelineView({ moduleId, nodeType }: PipelineViewProps) {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);

  const picklistName = PIPELINE_PICKLIST_MAP[moduleId] || '';
  const { data: picklist, isLoading: picklistLoading } = usePicklist(picklistName);
  const { data: nodesResponse, isLoading: nodesLoading } = useNodes(nodeType, { pageSize: 500 });
  const updateNode = useUpdateNode();

  const nodeConfig = NODE_TYPE_CONFIG[nodeType];
  const nodes = nodesResponse?.data || [];
  const stages = (picklist?.items || []).sort((a, b) => a.order - b.order);

  const handleCardClick = (node: Node) => {
    // Navigate to detail page
    const pluralMap: Record<string, string> = {
      Person: 'people',
      Opportunity: 'opportunities',
      FundraisingOpportunity: 'fundraising',
    };
    const pluralType = pluralMap[nodeType] || `${nodeType.toLowerCase()}s`;
    navigate(`/${pluralType}/${node.id}`);
  };

  const getNodesForStage = (stageId: string): Node[] => {
    return nodes.filter((n) => n.props.stage === stageId);
  };

  const getStageValue = (stageId: string): number => {
    return getNodesForStage(stageId).reduce((sum, n) => sum + ((n.props.amount as number) || 0), 0);
  };

  if (picklistLoading || nodesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!stages.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">No pipeline stages configured for module: {moduleId}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">{nodeConfig?.displayName || nodeType} Pipeline</Typography>
        <IconButton
          color="primary"
          onClick={() => {
            setSelectedNode(null);
            setDrawerOpen(true);
          }}
          sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
        >
          <Plus size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
        </IconButton>
      </Box>

      {/* Kanban columns */}
      <Box sx={{ flex: 1, display: 'flex', gap: 2, overflowX: 'auto', pb: 2 }}>
        {stages.map((stage) => {
          const stageNodes = getNodesForStage(stage.id);
          const totalValue = getStageValue(stage.id);
          const bgColor = stage.is_closed ? (stage.is_won ? 'success.light' : 'error.light') : 'grey.100';

          const isDragOver = dragOverStage === stage.id;
          return (
            <Paper
              key={stage.id}
              sx={{
                minWidth: 280,
                maxWidth: 280,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: bgColor,
                border: isDragOver ? '2px dashed' : '2px solid transparent',
                borderColor: isDragOver ? 'primary.main' : 'transparent',
                transition: 'border-color 0.2s, background-color 0.2s',
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOverStage(stage.id);
              }}
              onDragLeave={() => setDragOverStage(null)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOverStage(null);
                const nodeId = e.dataTransfer.getData('nodeId');
                const draggedNode = nodes.find((n) => n.id === nodeId);
                if (draggedNode && draggedNode.props.stage !== stage.id) {
                  updateNode.mutate({
                    id: draggedNode.id,
                    updates: { props: { ...draggedNode.props, stage: stage.id } },
                    nodeType,
                  });
                }
              }}
            >
              <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {stage.label}
                  </Typography>
                  <Chip label={stageNodes.length} size="small" />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  ${totalValue.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, p: 1, overflowY: 'auto' }}>
                {stageNodes.map((node) => {
                  const name = (node.props.name as string) || 'Untitled';
                  const amount = node.props.amount as number | undefined;
                  return (
                    <Card
                      key={node.id}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('nodeId', node.id);
                      }}
                      onClick={() => handleCardClick(node)}
                      sx={{
                        mb: 1,
                        cursor: 'grab',
                        '&:hover': { boxShadow: 3 },
                        '&:active': { cursor: 'grabbing' },
                      }}
                    >
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <GripVertical size={16} style={{ marginTop: 2, color: '#999' }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={500} noWrap>
                              {name}
                            </Typography>
                            {amount !== undefined && (
                              <Typography variant="caption" color="text.secondary">
                                ${amount.toLocaleString()}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            </Paper>
          );
        })}
      </Box>

      <NodeDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedNode(null);
        }}
        nodeType={nodeType}
        mode={selectedNode ? 'edit' : 'create'}
        node={selectedNode || undefined}
      />
    </Box>
  );
}
